import React, { useContext, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';

import { I18nContext } from '../context/translations/TranslationsContext.context';
import { Button } from '../components/ui/button';
import { enviarAlNavegadorDelSistema } from './navegadorExterno';
import { esApp, plataforma } from './plataforma';
import { URL_TIENDA, VersionMinima, comprobarVersion } from './versionMinima';

type Estado = 'comprobando' | 'dentro' | 'bloqueada';

/**
 * Si el binario instalado está por debajo de la versión mínima (`versionMinima.ts`), en lugar de la
 * app se pinta una pantalla que solo manda a la tienda. Se comprueba al abrir y cada vez que la app
 * vuelve a primer plano (por si se ha subido el mínimo, o ya ha actualizado).
 *
 * Envuelve a TODA la app —login y bloqueo biométrico incluidos— y, cuando bloquea, no los pinta: así
 * va por encima de cualquier otra pantalla sin pelear por el `z-index`. No se puede cerrar, y se marca
 * con `data-actualizacion-obligatoria` (también el logo de espera): con ella, el botón atrás de
 * Android (`botonAtras.ts`) cierra la app en vez de navegar por detrás.
 *
 * Al abrir, hasta saberlo (como mucho `ESPERA_MAXIMA_MS`), solo se pinta el logo: así Face ID no
 * pide la cara para luego taparla con «actualiza».
 *
 * Si no se sabe (`null`: sin red, timeout, respuesta rara) se queda como estaba: al abrir, entra
 * (fail-open); bloqueada, sigue bloqueada, para que quitar la red no sirva para saltárselo.
 *
 * Al quitar el bloqueo (solo pasa si se BAJA el mínimo con la app abierta: quien actualiza desde la
 * tienda vuelve a arrancar la app de cero), la app se vuelve a montar entera y Face ID pide la cara.
 * Se acepta: mantenerla montada debajo dejaría a Face ID asomar por encima de «actualiza».
 *
 * En la web pinta la app tal cual y no comprueba nada.
 */
const ActualizacionObligatoria: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { translate } = useContext(I18nContext).state;
	const [estado, setEstado] = useState<Estado>(() => (esApp() ? 'comprobando' : 'dentro'));
	// Solo cuenta la última comprobación: una vieja que llega tarde no pisa a la nueva.
	const ultima = useRef(0);

	useEffect(() => {
		if (!esApp()) {
			return undefined;
		}

		let montado = true;
		const comprobar = () => {
			const esta = ++ultima.current;
			// Un fallo inesperado cuenta como «no se sabe»: nunca dejar la app atascada en el logo.
			void comprobarVersion()
				.catch(() => null)
				.then((hayQue) => {
					if (!montado || esta !== ultima.current) {
						return;
					}
					if (hayQue === null) {
						setEstado((antes) => (antes === 'comprobando' ? 'dentro' : antes));
					} else {
						setEstado(hayQue ? 'bloqueada' : 'dentro');
					}
				});
		};

		comprobar();
		const escucha = App.addListener('appStateChange', ({ isActive }) => {
			if (isActive) {
				comprobar();
			}
		});

		return () => {
			montado = false;
			void escucha.then((handle) => handle.remove());
		};
	}, []);

	if (estado === 'dentro') {
		return <>{children}</>;
	}

	if (estado === 'comprobando') {
		return (
			<div
				data-actualizacion-obligatoria
				className="fixed inset-0 flex items-center justify-center bg-background"
			>
				<img src="/imgs/LOGO_VERDE_2.svg" alt="" className="h-12" />
			</div>
		);
	}

	return (
		<div
			role="alertdialog"
			data-state="open"
			data-actualizacion-obligatoria
			aria-modal="true"
			aria-labelledby="actualizacion-obligatoria-titulo"
			// `inset-y-0` y no `inset-0`: deja la pantalla fuera de las reglas de alto y centrado de los
			// diálogos de `index.css`, que la recortaban y dejaban asomar la app por debajo.
			className="fixed inset-x-0 inset-y-0 flex flex-col items-center justify-center gap-6 bg-background px-6 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] text-center"
		>
			<img src="/imgs/LOGO_VERDE_2.svg" alt="" className="h-12" />
			<div className="flex flex-col gap-2">
				<h1 id="actualizacion-obligatoria-titulo" className="font-heading text-xl font-semibold">
					{translate('movil.actualizacion.titulo')}
				</h1>
				<p className="text-muted-foreground">{translate('movil.actualizacion.texto')}</p>
			</div>
			<div className="flex w-full max-w-xs flex-col gap-3">
				<Button
					shape="pill"
					onClick={() =>
						enviarAlNavegadorDelSistema(URL_TIENDA[plataforma() as keyof VersionMinima])
					}
				>
					{translate('movil.actualizacion.boton')}
				</Button>
			</div>
		</div>
	);
};

export default ActualizacionObligatoria;
